import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Linkedin, Mail, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEOHead } from '../components/SEOHead';
import { team, values, company } from '../content/site';
import { getTeamImage } from '../content/images';
import { useEditor } from '../contexts/EditorContext';

export default function TeamPage() {
  const { getValue } = useEditor();
  const e = getValue || ((_, fb) => fb);

  return (
    <div data-testid="team-page" className="overflow-hidden">
      <SEOHead page="team" />
      {/* Hero Section */}
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-black/[0.03] rounded-full blur-[120px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-black/[0.02] rounded-full blur-[100px]" />
        </div>

        <div className="relative max-w-[1200px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-20"
          >
            <p className="text-sm font-medium text-[#9A9A9A] uppercase tracking-wider mb-4">
              {e('team.heroLabel', 'Über uns')}
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mb-6">
              {e('team.heroTitle', 'Das Team hinter VISUWORKS')}
            </h1>
            <p className="text-lg md:text-xl text-[#1A1A1A]/70">
              {e('team.heroDesc', 'Experten aus Design, Produktion und Projektmanagement – vereint in der Leidenschaft für visuelle Exzellenz.')}
            </p>
          </motion.div>

          {/* Team Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {team.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group"
              >
                <div className="relative rounded-[24px] bg-[#E8E4DD] border border-black/[0.07] hover:border-black/[0.15] transition-all duration-300 overflow-hidden">
                  {/* Avatar Placeholder */}
                  <div className="aspect-square bg-gradient-to-br from-indigo-500/10 to-purple-500/10 flex items-center justify-center">
                    <div className="w-24 h-24 rounded-full bg-black/[0.03] border border-black/[0.07] flex items-center justify-center">
                      <Users className="w-12 h-12 text-[#9A9A9A]/60" />
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-6">
                    <h3 className="text-lg font-bold mb-1">{e(`team.members.${index}.name`, member.name)}</h3>
                    <p className="text-sm text-[#9A9A9A] mb-3">{e(`team.members.${index}.role`, member.role)}</p>
                    <p className="text-sm text-[#6B6B6B] mb-4">{e(`team.members.${index}.bio`, member.bio)}</p>

                    {/* Social Links */}
                    <div className="flex items-center gap-2">
                      <a
                        href={member.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-9 h-9 rounded-lg bg-black/[0.03] border border-black/[0.07] flex items-center justify-center text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-black/[0.05] transition-all duration-200"
                        aria-label={`${member.name} auf LinkedIn`}
                      >
                        <Linkedin className="w-4 h-4" />
                      </a>
                      <a
                        href={`mailto:${member.email}`}
                        className="w-9 h-9 rounded-lg bg-black/[0.03] border border-black/[0.07] flex items-center justify-center text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-black/[0.05] transition-all duration-200"
                        aria-label={`E-Mail an ${member.name}`}
                      >
                        <Mail className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 md:py-32 bg-black/[0.02]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-sm font-medium text-[#9A9A9A] uppercase tracking-wider mb-4">
              {e('team.valuesLabel', 'Werte')}
            </p>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
              {e('team.valuesTitle', 'Wofür wir stehen')}
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-[20px] bg-black/[0.02] border border-black/[0.07]"
              >
                <h3 className="text-lg font-bold mb-2">{e(`team.values.${index}.title`, value.title)}</h3>
                <p className="text-sm text-[#6B6B6B]">{e(`team.values.${index}.desc`, value.description)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Info */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-sm font-medium text-[#9A9A9A] uppercase tracking-wider mb-4">
                {e('team.companyLabel', 'Unternehmen')}
              </p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
                {e('team.companyTitle', '15 Jahre Erfahrung im Premium-Segment')}
              </h2>
              <div className="space-y-4 text-[#1A1A1A]/70">
                <p>
                  {e('team.companyText1', 'VISUWORKS wurde 2010 in Düsseldorf gegründet mit dem Ziel, visuelle Markenlösungen auf höchstem Niveau zu realisieren. Was als kleine Werkstatt begann, ist heute ein Full-Service-Partner für Unternehmen in ganz Europa.')}
                </p>
                <p>
                  {e('team.companyText2', 'Unser Standort in Düsseldorf ist das Zentrum unserer Aktivitäten, aber unsere Projekte führen uns durch ganz Europa – von der kleinen Fahrzeugfolierung bis zum internationalen Flotten-Rollout.')}
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <div className="aspect-[4/3] rounded-[28px] overflow-hidden bg-gradient-to-br from-black/[0.03] to-black/0 border border-black/[0.07]">
                <div className="h-full flex items-center justify-center">
                  <p className="text-[#9A9A9A] text-sm">{e('team.companyImageAlt', 'Unternehmensstandort Düsseldorf')}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-black/[0.02]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-[28px] overflow-hidden bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-black/[0.07] p-12 md:p-20"
          >
            <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-black/[0.03] rounded-full blur-[100px]" />
            <div className="relative text-center max-w-2xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
                {e('team.ctaTitle', 'Zusammenarbeit starten?')}
              </h2>
              <p className="text-lg text-[#6B6B6B] mb-10">
                {e('team.ctaDesc', 'Lernen Sie uns kennen – wir freuen uns auf Ihr Projekt.')}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/kontakt">
                  <Button size="lg">
                    {e('team.ctaButtonLabel', 'Kontakt aufnehmen')}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/projekte">
                  <Button variant="secondary" size="lg">
                    {e('team.ctaButton2Label', 'Projekte ansehen')}
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
